/**
 * GemstoneDetails custom element for handling gemstone details
 * 
 * @module gemstone-details
 * @version 1.0.0
 * @extends HTMLElement
 */
class GemstoneDetails extends HTMLElement {

  /**
   * Constructor for the GemstoneDetails element
   */
  constructor() {

    super();

    this.data = {};

    this.enableLoading();
  }

  /**
   * Connected callback
   */
  async connectedCallback() {

    const params = new URL(window.location).searchParams;
    const id = params.get('id') || window.theme.stones.getIdFromSlug(params.get('stone') || '');

    this.data = await theme.stones.getStoneById(id, 'gemstone');

    this.render();

    this.initEvents();

    this.disableLoading();
  }

  /**
   * Render html structure
   */
  render() {

    this.innerHTML = `
      <div class="bg-secondary-200 px-4 py-7 tablet:py-20 desktop:py-24">

        <div class="container">

          <div class="flex flex-col tablet:flex-row tablet:gap-10">

            <div class="tablet:w-2/5">

              <component-accordion id="product_details_about" class="group border-b border-primary-100 px-5 py-1 tablet:border-none">

                <div class="py-4 flex items-center gap-2 justify-between" data-accordion-trigger>
                  <div class="flex gap-2 items-center">
                    <span class="w-5 h-5 tablet:hidden">
                      <svg width="100%" height="100%" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12.5 2.5L17.5 7.5L6 19L2 18L3 14L12.5 2.5Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
                        <path d="M11 4L16 9" stroke="currentColor" stroke-width="1.5"/>
                        <path d="M2 18L6 19" stroke="currentColor" stroke-width="1.5"/>
                      </svg>
                    </span>
                    <span class="tablet:!inline tablet:text-2xl">About This Stone</span>
                  </div>
                  <span class="w-5 h-5 group-[.active]:hidden tablet:!hidden">
                    <svg width="100%" height="100%" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M10 4.25C10.4142 4.25 10.75 4.58579 10.75 5V9.25H15C15.4142 9.25 15.75 9.58579 15.75 10C15.75 10.4142 15.4142 10.75 15 10.75H10.75V15C10.75 15.4142 10.4142 15.75 10 15.75C9.58579 15.75 9.25 15.4142 9.25 15V10.75L5 10.75C4.58579 10.75 4.25 10.4142 4.25 10C4.25 9.58579 4.58579 9.25 5 9.25L9.25 9.25V5C9.25 4.58579 9.58579 4.25 10 4.25Z" fill="currentColor" />
                    </svg>
                  </span>
                  <span class="w-5 h-5 hidden group-[.active]:block tablet:!hidden">
                    <svg width="100%" height="100%" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M4.25 10C4.25 9.58579 4.58579 9.25 5 9.25H15C15.4142 9.25 15.75 9.58579 15.75 10C15.75 10.4142 15.4142 10.75 15 10.75H5C4.58579 10.75 4.25 10.4142 4.25 10Z" fill="currentColor" />
                    </svg>
                  </span>
                </div>

                <div data-accordion-container>

                  <div class="flex flex-col gap-3 pb-3">

                    <div class="flex gap-2">
                      <span class="font-medium">Style number</span>
                      <span>${this.data.sku || 'N/A'}</span>
                    </div>

                    <div class="rte">${this.data.description}</div>

                    <div class="flex gap-2 items-center">
                      <div class="size-7">
                        <svg
                          width="100%"
                          height="100%"
                          viewBox="0 0 29 26"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg">
                          <path d="M2.25 7.25V14.75H21.75V13.25H12.75V11.75H21.75V10.25H12.75V8.75H21.75V7.25H2.25ZM3.75 8C3.94891 8 4.13968 8.07902 4.28033 8.21967C4.42098 8.36032 4.5 8.55109 4.5 8.75C4.5 8.94891 4.42098 9.13968 4.28033 9.28033C4.13968 9.42098 3.94891 9.5 3.75 9.5C3.55109 9.5 3.36032 9.42098 3.21967 9.28033C3.07902 9.13968 3 8.94891 3 8.75C3 8.55109 3.07902 8.36032 3.21967 8.21967C3.36032 8.07902 3.55109 8 3.75 8ZM6.75 8C6.94891 8 7.13968 8.07902 7.28033 8.21967C7.42098 8.36032 7.5 8.55109 7.5 8.75C7.5 8.94891 7.42098 9.13968 7.28033 9.28033C7.13968 9.42098 6.94891 9.5 6.75 9.5C6.55109 9.5 6.36032 9.42098 6.21967 9.28033C6.07902 9.13968 6 8.94891 6 8.75C6 8.55109 6.07902 8.36032 6.21967 8.21967C6.36032 8.07902 6.55109 8 6.75 8ZM9.75 8C9.94891 8 10.1397 8.07902 10.2803 8.21967C10.421 8.36032 10.5 8.55109 10.5 8.75C10.5 8.94891 10.421 9.13968 10.2803 9.28033C10.1397 9.42098 9.94891 9.5 9.75 9.5C9.55109 9.5 9.36032 9.42098 9.21967 9.28033C9.07902 9.13968 9 8.94891 9 8.75C9 8.55109 9.07902 8.36032 9.21967 8.21967C9.36032 8.07902 9.55109 8 9.75 8ZM5.25 10.25C5.44891 10.25 5.63968 10.329 5.78033 10.4697C5.92098 10.6103 6 10.8011 6 11C6 11.1989 5.92098 11.3897 5.78033 11.5303C5.63968 11.671 5.44891 11.75 5.25 11.75C5.05109 11.75 4.86032 11.671 4.71967 11.5303C4.57902 11.3897 4.5 11.1989 4.5 11C4.5 10.8011 4.57902 10.6103 4.71967 10.4697C4.86032 10.329 5.05109 10.25 5.25 10.25ZM8.25 10.25C8.44891 10.25 8.63968 10.329 8.78033 10.4697C8.92098 10.6103 9 10.8011 9 11C9 11.1989 8.92098 11.3897 8.78033 11.5303C8.63968 11.671 8.44891 11.75 8.25 11.75C8.05109 11.75 7.86032 11.671 7.71967 11.5303C7.57902 11.3897 7.5 11.1989 7.5 11C7.5 10.8011 7.57902 10.6103 7.71967 10.4697C7.86032 10.329 8.05109 10.25 8.25 10.25ZM11.25 10.25C11.4489 10.25 11.6397 10.329 11.7803 10.4697C11.921 10.6103 12 10.8011 12 11C12 11.1989 11.921 11.3897 11.7803 11.5303C11.6397 11.671 11.4489 11.75 11.25 11.75C11.0511 11.75 10.8603 11.671 10.7197 11.5303C10.579 11.3897 10.5 11.1989 10.5 11C10.5 10.8011 10.579 10.6103 10.7197 10.4697C10.8603 10.329 11.0511 10.25 11.25 10.25ZM3.75 12.5C3.94891 12.5 4.13968 12.579 4.28033 12.7197C4.42098 12.8603 4.5 13.0511 4.5 13.25C4.5 13.4489 4.42098 13.6397 4.28033 13.7803C4.13968 13.921 3.94891 14 3.75 14C3.55109 14 3.36032 13.921 3.21967 13.7803C3.07902 13.6397 3 13.4489 3 13.25C3 13.0511 3.07902 12.8603 3.21967 12.7197C3.36032 12.579 3.55109 12.5 3.75 12.5ZM6.75 12.5C6.94891 12.5 7.13968 12.579 7.28033 12.7197C7.42098 12.8603 7.5 13.0511 7.5 13.25C7.5 13.4489 7.42098 13.6397 7.28033 13.7803C7.13968 13.921 6.94891 14 6.75 14C6.55109 14 6.36032 13.921 6.21967 13.7803C6.07902 13.6397 6 13.4489 6 13.25C6 13.0511 6.07902 12.8603 6.21967 12.7197C6.36032 12.579 6.55109 12.5 6.75 12.5ZM9.75 12.5C9.94891 12.5 10.1397 12.579 10.2803 12.7197C10.421 12.8603 10.5 13.0511 10.5 13.25C10.5 13.4489 10.421 13.6397 10.2803 13.7803C10.1397 13.921 9.94891 14 9.75 14C9.55109 14 9.36032 13.921 9.21967 13.7803C9.07902 13.6397 9 13.4489 9 13.25C9 13.0511 9.07902 12.8603 9.21967 12.7197C9.36032 12.579 9.55109 12.5 9.75 12.5ZM2.25 16.25V17.75H21.75V16.25H2.25ZM2.25 19.25V20.75H21.75V19.25H2.25Z" fill="#231F20" />
                          <path d="M15.8787 4.21199C14.7071 5.38357 14.7071 7.28306 15.8787 8.45463L21 13.576L26.1213 8.45463C27.2929 7.28306 27.2929 5.38357 26.1213 4.21199C24.9497 3.04042 23.0503 3.04042 21.8787 4.21199L21 5.09071L20.1213 4.21199C18.9497 3.04042 17.0503 3.04042 15.8787 4.21199Z" fill="#CB9FAC" />
                        </svg>
                      </div>
                      <span class="uppercase font-medium text-sm leading-[14px]">Crafted in the USA</span>
                    </div>

                  </div>

                </div>

              </component-accordion>

            </div>

            <div class="tablet:w-3/5 tablet:mt-12">

              <component-accordion open class="group border-primary-100 px-5 py-1">

                <div class="py-4 flex items-center gap-2 justify-between" data-accordion-trigger>
                  <div class="flex gap-2 items-center">
                    <span class="w-5 h-5">
                      <svg
                        width="100%"
                        height="100%"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M2.29157 6.66675H18.5416M1.80016 7.1437L9.9154 17.6695C9.97437 17.7464 10.0503 17.8087 10.1372 17.8515C10.2241 17.8944 10.3197 17.9167 10.4166 17.9167C10.5135 17.9167 10.6091 17.8944 10.696 17.8515C10.7829 17.8087 10.8588 17.7464 10.9177 17.6695L19.033 7.1437C19.1128 7.03981 19.1591 6.9141 19.1657 6.78327C19.1723 6.65245 19.139 6.5227 19.0701 6.41128L16.3173 1.96792C16.2607 1.87597 16.1814 1.80003 16.0872 1.74732C15.9929 1.6946 15.8867 1.66687 15.7787 1.66675H5.05446C4.94644 1.66687 4.84025 1.6946 4.74598 1.74732C4.6517 1.80003 4.57246 1.87597 4.51579 1.96792L1.76305 6.41128C1.69417 6.5227 1.66082 6.65245 1.66745 6.78327C1.67408 6.9141 1.72037 7.03981 1.80016 7.1437Z"
                          stroke="currentColor"
                          stroke-linecap="round"
                          stroke-linejoin="round" />
                        <path
                          d="M16.0416 2.29175L14.1666 6.66675M14.1666 6.66675L10.4166 1.66675L6.66663 6.66675M14.1666 6.66675L10.4166 17.2917L6.66663 6.66675M4.79163 2.29175L6.66663 6.66675"
                          stroke="currentColor"
                          stroke-linecap="round"
                          stroke-linejoin="round" />
                      </svg>
                    </span>
                    <span>Stone Information</span>
                  </div>
                  <span class="w-5 h-5 group-[.active]:hidden">
                    <svg width="100%" height="100%" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M10 4.25C10.4142 4.25 10.75 4.58579 10.75 5V9.25H15C15.4142 9.25 15.75 9.58579 15.75 10C15.75 10.4142 15.4142 10.75 15 10.75H10.75V15C10.75 15.4142 10.4142 15.75 10 15.75C9.58579 15.75 9.25 15.4142 9.25 15V10.75L5 10.75C4.58579 10.75 4.25 10.4142 4.25 10C4.25 9.58579 4.58579 9.25 5 9.25L9.25 9.25V5C9.25 4.58579 9.58579 4.25 10 4.25Z" fill="currentColor" />
                    </svg>
                  </span>
                  <span class="w-5 h-5 hidden group-[.active]:block">
                    <svg width="100%" height="100%" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M4.25 10C4.25 9.58579 4.58579 9.25 5 9.25H15C15.4142 9.25 15.75 9.58579 15.75 10C15.75 10.4142 15.4142 10.75 15 10.75H5C4.58579 10.75 4.25 10.4142 4.25 10Z" fill="currentColor" />
                    </svg>
                  </span>
                </div>

                <div data-accordion-container>

                  <div class="flex flex-col pb-3 gap-3">

                    <div class="flex flex-col gap-3">

                      <div class="flex gap-3">
                        <span class="font-medium w-1/2">Price</span>
                        <span class="w-1/2">${theme.money.format(this.data.price, true)}</span>
                      </div>

                      <div class="flex gap-3">
                        <span class="font-medium w-1/2">Shape</span>
                        <span class="w-1/2">${this.data.shape}</span>
                      </div>

                      <div class="flex gap-3">
                        <span class="font-medium w-1/2">Carat Weight</span>
                        <span class="w-1/2">${this.data.carat}</span>
                      </div>

                      <div class="flex gap-3">
                        <span class="font-medium w-1/2">Color</span>
                        <span class="w-1/2">${this.data.color}</span>
                      </div>

                    </div>

                    <div class="flex flex-col gap-3">

                      <div class="flex gap-3">
                        <span class="font-medium w-1/2">Approximate Measurement</span>
                        <span class="w-1/2">${this.data.gem_size}</span>
                      </div>

                      <div class="flex gap-3">
                        <span class="font-medium w-1/2">Quality</span>
                        <span class="w-1/2">${this.data.gem_grade}</span>
                      </div>

                    </div>

                  </div>

                </div>

              </component-accordion>

            </div>

          </div>

        </div>

      </div>
    `;
  }

  /**
   * Initialize events
   */
  initEvents() {
    // Handle accordion behavior for breakpoints
    const productAccordion = this.querySelector('#product_details_about');
    
    if (productAccordion) {
      // Handle initial state
      this.handleBreakpoint();

      // Listen for breakpoint changes
      window.addEventListener('screen-breakpoint', (event) => this.handleBreakpoint(event));
    }
  }

  /**
   * Handle breakpoint changes for accordion behavior
   */
  handleBreakpoint(event) {
    const productAccordion = this.querySelector('#product_details_about');
    if (!productAccordion) return;

    const currentMode = event ? event.detail.mode : window.screenMode;
    const isTabletOrAbove = ['tablet', 'laptop', 'desktop', 'wide'].includes(currentMode);

    if (isTabletOrAbove) {
      productAccordion.disable();
    } else {
      productAccordion.enable();
    }
  }

  /**
   * Enable the loading state
   */
  enableLoading() {
    this.classList.add('loading');

    if (window.theme.loading) {
      window.theme.loading.enable();
    }
  }

  /**
   * Disable the loading state
   */
  disableLoading() {
    this.classList.remove('loading');

    if (window.theme.loading) {
      window.theme.loading.disable();
    }
  }

}

customElements.define('gemstone-details', GemstoneDetails);
